import React, { useState, useEffect, useRef } from 'react';
import Confetti from 'react-confetti'; // Import Confetti
import { HelpCircle, Award, User, Calendar, Trophy, Settings } from 'lucide-react';
import { WaterBottle } from '../components/WaterBottle';
import { CameraDetection } from '../components/CameraDetection';
import { WaterTimer } from '../components/WaterTimer';
import { Instructions } from '../components/Instructions';
import { MonthlyStats } from '../components/MonthlyStats';
import { Badges } from '../components/Badges';
import { useWaterStore } from '../store/useWaterStore';
import { useAuthStore } from '../store/useAuthStore';
import { useDevStore } from '../store/useDevStore';
import { getLocalDateString } from '../utils/formatDate';

export const Dashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'instructions' | 'streaks' | 'history' | 'account' | 'badges'>('instructions');
  const { currentAmount, dailyGoal, streak, checkAndUpdateStreak } = useWaterStore();
  const { user, userData, logout, isLoading, isGuest } = useAuthStore();
  const { isDevMode, toggleDevMode } = useDevStore();
  const [showConfetti, setShowConfetti] = useState(false); // State for confetti

  // Initialize water store with Firebase data
  useEffect(() => {
    if (userData) {
      useWaterStore.setState({
        currentAmount: userData.waterIntake,
        dailyGoal: userData.dailyGoal,
        reminderInterval: userData.reminderInterval,
        streak: userData.streak || 0,
        lastCheckDate: userData.lastCheckDate || getLocalDateString(new Date()),
        monthlyStats: userData.monthlyStats || {},
        drinkHistory: userData.drinkHistory || [],
      });
    }
  }, [userData]);

  // Check streak and reset at midnight
  useEffect(() => {
    // Initial check
    checkAndUpdateStreak();

    // Set up interval for checking
    const interval = setInterval(() => {
      checkAndUpdateStreak();
    }, 60000); // Check every minute

    return () => clearInterval(interval);
  }, [checkAndUpdateStreak]);

  const hasCelebrated = useRef(false);

  // Trigger confetti when daily goal is reached
 useEffect(() => {
    if (currentAmount >= dailyGoal && dailyGoal > 0) {
      if (!hasCelebrated.current) {
        setShowConfetti(true);
        hasCelebrated.current = true;
      }
    } else {
      hasCelebrated.current = false;
    }
  }, [currentAmount, dailyGoal]);

  const renderSidePanel = () => {
    switch (activeTab) {
      case 'instructions':
        return <Instructions />;
      case 'streaks':
        return (
          <div className="p-4">
            <h3 className="text-lg font-semibold mb-4">Your Streaks</h3>
            <div className="text-3xl font-bold text-blue-500 text-center">
              {streak} days
            </div>
          </div>
        );
      case 'history':
        return <MonthlyStats />;
      case 'account':
        return (
          <div className="p-4">
            <h3 className="text-lg font-semibold mb-4">Account Settings</h3>
            <div className="space-y-4">
              <p className="text-gray-600">{user?.email}</p>
              
              {/* Show different content for guest users */}
              {isGuest ? (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h4 className="font-medium text-yellow-800 mb-2">Guest Mode</h4>
                  <p className="text-yellow-700 text-sm mb-3">You're currently using Hydrio as a guest. Sign in to save your progress and access all features.</p>
                  <button
                    onClick={() => {
                      // Redirect to login page
                      window.location.href = '/login';
                    }}
                    className="w-full px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm"
                  >
                    Sign In to Save Progress
                  </button>
                </div>
              ) : (
                <>
                  {/* Daily Goal Setting */}
                  <div className="border-t pt-4">
                    <h4 className="font-medium mb-2">Daily Water Goal</h4>
                    <div className="flex items-center space-x-2">
                      <input
                        type="number"
                        min="500"
                        max="1000"
                        defaultValue={dailyGoal}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                        id="dailyGoalInput"
                      />
                      <button
                        onClick={() => {
                          const input = document.getElementById('dailyGoalInput') as HTMLInputElement;
                          const newGoal = parseInt(input.value);
                          if (newGoal && newGoal >= 500 && newGoal <= 1000) {
                            useWaterStore.getState().setDailyGoal(newGoal);
                          }
                        }}
                        className="px-3 py-2 bg-blue-500 text-white rounded-md text-sm hover:bg-blue-600"
                      >
                        Save
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Enter goal in ml (500-1000)</p>
                  </div>

                  <button
                    onClick={() => logout()}
                    className="w-full px-4 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200"
                  >
                    Logout
                  </button>

                  <div className="border-t pt-4 mt-4">
                    <h4 className="font-medium mb-2 text-red-600">Danger Zone</h4>
                    <button
                      onClick={() => {
                        if (window.confirm("Are you sure you want to reset today's progress? This will clear your water intake and drink history for today.")) {
                          useWaterStore.getState().resetDaily();
                        }
                      }}
                      className="w-full px-4 py-2 border border-red-200 text-red-600 rounded-lg hover:bg-red-50 text-sm"
                    >
                      Reset Today's Progress
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        );
      case 'badges':
        return <Badges />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 flex">
      {showConfetti && (
        <Confetti
          recycle={false}
          numberOfPieces={500}
          onConfettiComplete={() => setShowConfetti(false)}
        />
      )} {/* Render Confetti */}
      {isLoading && (
        <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-50">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
            <p className="mt-2 text-blue-600 font-medium">Loading...</p>
          </div>
        </div>
      )}
      {/* Side panel */}
      <div className="w-96 bg-white shadow-lg">
        <div className="p-4 border-b">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold">Help & Info</h2>
            {isGuest && (
              <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full">
                Guest Mode
              </span>
            )}
          </div>
        </div>
        <div className="flex border-b">
          <button
            className={`flex-1 p-2 text-sm ${activeTab === 'instructions' ? 'text-blue-500 border-b-2 border-blue-500' : 'text-gray-600'}`}
            onClick={() => setActiveTab('instructions')}
          >
            <HelpCircle className="h-4 w-4 mx-auto mb-1" />
            Instructions
          </button>
          <button
            className={`flex-1 p-2 text-sm ${activeTab === 'streaks' ? 'text-blue-500 border-b-2 border-blue-500' : 'text-gray-600'}`}
            onClick={() => setActiveTab('streaks')}
          >
            <Award className="h-4 w-4 mx-auto mb-1" />
            Streaks
          </button>
          <button
            className={`flex-1 p-2 text-sm ${activeTab === 'account' ? 'text-blue-500 border-b-2 border-blue-500' : 'text-gray-600'}`}
            onClick={() => setActiveTab('account')}
          >
            <User className="h-4 w-4 mx-auto mb-1" />
            Account
          </button>
          <button
            className={`flex-1 p-2 text-sm ${activeTab === 'history' ? 'text-blue-500 border-b-2 border-blue-500' : 'text-gray-600'}`}
            onClick={() => setActiveTab('history')}
          >
            <Calendar className="h-4 w-4 mx-auto mb-1" />
            History
          </button>
          <button
            className={`flex-1 p-2 text-sm ${activeTab === 'badges' ? 'text-blue-500 border-b-2 border-blue-500' : 'text-gray-600'}`}
            onClick={() => setActiveTab('badges')}
          >
            <Trophy className="h-4 w-4 mx-auto mb-1" />
            Badges
          </button>
        </div>
        {renderSidePanel()}
      </div>

      <div className="flex-1 p-8">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white p-6 rounded-xl shadow-lg">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-center">Your Progress</h2>
                {isGuest && (
                  <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full">
                    Guest Mode
                  </span>
                )}
              </div>
              <WaterBottle />
              <div className="mt-4 text-center">
                <p className="text-lg">Today's intake: {(currentAmount / 1000).toFixed(1)}L</p>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg">
              <h2 className="text-2xl font-bold mb-4 text-center">Camera Detection</h2>
              <CameraDetection />
            </div>

            <div className="md:col-span-2">
              <WaterTimer />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};